import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, FileSignature, Lock, AlertCircle, ArrowLeft } from "lucide-react";
import { lookupExamPass, createCheckout } from "../lib/api";
import { useClinic } from "../context/ClinicContext";
import PayPalCheckout from "../components/PayPalCheckout";

const CertificatePurchase = () => {
  const { token } = useParams();
  const { pricing } = useClinic();
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, valid: false, data: null, error: "" });
  const [busy, setBusy] = useState(false);
  const certPrice = pricing?.diploma_certificate?.amount ?? 150;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await lookupExamPass(token);
        if (alive) setState({ loading: false, valid: true, data: d, error: "" });
      } catch (e) {
        if (alive) setState({
          loading: false, valid: false, data: null,
          error: e?.response?.data?.detail || "This link is invalid or has expired.",
        });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const handleBuy = async () => {
    setBusy(true);
    try {
      const res = await createCheckout({
        product_type: "diploma_certificate",
        cert_token: token,
        full_name: state.data?.full_name,
        email: state.data?.email,
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not start checkout.");
      setBusy(false);
    }
  };

  return (
    <div data-testid="cert-purchase-page" className="max-w-3xl mx-auto px-4 sm:px-8 py-16 sm:py-24">
      <Link to="/" className="inline-flex items-center gap-2 text-mute text-xs hover:text-forest mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to home
      </Link>
      <div className="clay-surface rounded-3xl p-8 sm:p-12 text-center">
        {state.loading ? (
          <div className="py-10 text-mute"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : !state.valid ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-clay/10 text-clay mx-auto flex items-center justify-center mb-4">
              <AlertCircle className="w-6 h-6" />
            </div>
            <h1 className="font-heading text-3xl text-forest font-bold mb-2">Link not recognised</h1>
            <p className="text-mute text-sm">{state.error}</p>
          </>
        ) : state.data.cert_purchased ? (
          <>
            <div className="w-14 h-14 rounded-2xl bg-forest/10 text-forest mx-auto flex items-center justify-center mb-4">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <h1 className="font-heading text-3xl text-forest font-bold mb-2">Already purchased</h1>
            <p className="text-mute text-sm leading-relaxed">
              You've already paid for your certificate. We'll send the signed &amp; stamped PDF to <strong className="text-ink">{state.data.email}</strong> shortly after the Sheikh signs it.
            </p>
          </>
        ) : (
          <>
            <div className="w-14 h-14 rounded-2xl bg-gold/15 text-gold mx-auto flex items-center justify-center mb-4">
              <FileSignature className="w-6 h-6" />
            </div>
            <p className="text-xs uppercase tracking-[0.25em] text-gold mb-3">Congratulations — you passed</p>
            <h1 className="font-heading text-4xl text-forest font-bold mb-2">Claim your Diploma</h1>
            <p className="text-mute leading-relaxed mb-6">
              Salām <strong className="text-ink">{state.data.full_name}</strong>! You've successfully passed your
              practical exam. Pay below to receive your <strong>signed &amp; stamped Hijama Diploma certificate</strong>
              (high-resolution PDF) at <strong className="text-ink">{state.data.email}</strong>.
            </p>
            <div className="font-heading text-5xl text-forest font-bold mb-6">£{certPrice.toFixed(2)}</div>
            <button
              data-testid="cert-purchase-btn"
              onClick={handleBuy}
              disabled={busy}
              className="inline-flex items-center justify-center gap-2 bg-forest text-sand hover:bg-sage rounded-full px-8 py-3.5 text-sm font-medium transition disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              {busy ? "Redirecting to Stripe…" : "Pay with card (Stripe)"}
            </button>
            <div className="mt-4 max-w-xs mx-auto">
              <PayPalCheckout
                productType="diploma_certificate"
                payload={{ cert_token: token, full_name: state.data?.full_name, email: state.data?.email }}
                onPaid={({ order_id }) => nav(`/payment-success?session_id=${order_id}`)}
              />
            </div>
            <p className="text-[11px] text-mute mt-4">
              You'll receive the certificate by email within 3–5 business days after the Sheikh signs & stamps it.
            </p>
          </>
        )}
      </div>
    </div>
  );
};

export default CertificatePurchase;
